// Contact-record Email panel (Phase 5 Gmail). Live-fetches the VIEWER's own
// Gmail threads with this contact — displayed, never stored, so nobody sees
// anyone else's inbox. Sends from the hub are logged via onLogEmail (wired to
// the shared activities timeline by the host). SSR renders the disconnected
// hint; the fetch runs only in an effect.
import { useEffect, useState } from 'react'
import { useGoogle } from '../lib/useGoogle.js'
import { gmailListThreads, hasConnectedBefore } from '../lib/google.js'
import { normalizeEmail } from '../lib/format.js'
import ComposeEmailModal from './ComposeEmailModal.jsx'

export default function EmailPanel({ contactEmail, onLogEmail, templates = [], recipients = [], contact = null, engagement = null, sender = null, emptyHint }) {
  const g = useGoogle()
  const [threads, setThreads] = useState(null) // null = not fetched/loading
  const [error, setError] = useState(null)
  const [composing, setComposing] = useState(false)

  // On an engagement the address comes from its linked contacts; on a contact
  // record it is the contact's own. Either way ONE address is fetched and shown,
  // because a mailbox view for several people at once would be a privacy leak.
  const email = normalizeEmail(contactEmail || recipients[0]?.email)
  const connected = g.status === 'connected' || (typeof window !== 'undefined' && hasConnectedBefore())

  useEffect(() => {
    if (typeof window === 'undefined' || !email || !hasConnectedBefore()) return
    let alive = true
    // Reset before fetching so a re-render with a different contact can never
    // show the previous contact's threads while the new fetch is in flight.
    setThreads(null)
    setError(null)
    gmailListThreads(email, { maxResults: 8 })
      .then((ts) => { if (alive) { setThreads(ts); setError(null) } })
      .catch((e) => { if (alive) setError(e.message) })
    return () => { alive = false }
  }, [email, g.status])

  const fmtWhen = (s) => {
    const d = new Date(s)
    return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span className="tag-label" style={{ color: 'var(--color-muted)' }}>Email</span>
        <div style={{ flex: 1 }} />
        {(email || recipients.length > 0) && (
          <button onClick={() => setComposing(true)} className="btn btn-ghost" style={{ fontSize: 11, padding: '5px 10px' }}>Compose</button>
        )}
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--color-muted)', marginBottom: 10 }}>
        Your mailbox, viewed live — nothing is stored or shared. Only emails sent from the hub land on the timeline.
      </div>

      {!email && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{emptyHint || 'Add an email address to this contact to see history.'}</div>}
      {email && !connected && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Connect Google in Settings to see your email history here.</div>}
      {email && connected && error && <div style={{ fontSize: 12, color: 'var(--color-danger)' }}>{error}</div>}
      {email && connected && !error && threads === null && (
        <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>Loading…</div>
      )}
      {email && connected && !error && threads && threads.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>No email history with this contact in your mailbox.</div>
      )}
      {email && connected && !error && (threads || []).map((t) => (
        <a key={t.id} href={t.link} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '8px 0', borderBottom: '1px solid var(--surface-row)', textDecoration: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--color-white)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
            {t.messageCount > 1 && <span className="font-mono" style={{ fontSize: 10, color: 'var(--color-muted)' }}>{t.messageCount}</span>}
            <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--color-muted)' }}>{fmtWhen(t.date)}</span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--color-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{t.snippet}</div>
        </a>
      ))}

      {composing && (
        <ComposeEmailModal
          to={recipients.length > 0 ? undefined : email}
          recipients={recipients}
          templates={templates}
          contact={contact}
          engagement={engagement}
          sender={sender}
          onClose={() => setComposing(false)}
          onLogEmail={onLogEmail}
        />
      )}
    </div>
  )
}
